import { MISSING_PAGE_DATA_MESSAGE } from '@layers/web/constants/error.ts'

export const IGNORE_ERRORS: Array<string | RegExp> = [
  // Redundant errors (avoid extending)
  'Hydration completed but contains mismatches', // TODO: This should not be ignored. Needs to be fixed
  'replaceState', // This should not be ignored
  'TypeMismatchError',
  'ResizeObserver',
  'webkitExitFullScreen',
  'ReportingObserver',
  'HierarchyRequestError',
  'The operation is insecure',
  'in strict mode',
  'Blocked a frame with origin',
  'Ignored attempt to cancel',
  'instantSearchSDKJSBridgeClearHighlight',
  'ibFindAllVideos',
  'loadDownloads',
  'processRandomSelector',
  'Illegal invocation',
  'global code',
  'Decoding failed',
  'Failed to fetch',
  'PaymentAutofillConfig',
  'Navigation cancelled',
  'messagehandlers',
  'Non-Error promise rejection captured',
  'Load failed',
  'webkit.messageHandlers',
  'weixinPostMessageHandlers', // WeChat in-app browser's injected bridge
  'Aborted',
  'AbortError',
  'captured as promise rejection',
  'IFrameMessageHandler',
  'The operation was aborted',
  'No error message',
  'Request signal is aborted',
  'The user aborted a request',
  'The operation was aborted due to timeout',
  'Network Error',
  "Failed to read the 'localStorage' property from 'Window': Access is denied for this document",
  'HTTP Client Error with status code',
  'FetchError',
  '[GET]',
  '[POST]',
  'Persisted state failed',
  'NetworkError when attempting to fetch resource',
  'A network error occured',

  // Errors that we can't do much about and rarely happens
  'Unreachable hosts - your application id may be incorrect.',
  'Failed to initialize WebGL',
  'reloadModels', // mapbox-gl's webglcontextrestored handler on a canvas whose Map constructor threw at the lost context (guarded in Mapbox.vue): the half-built map has no style
  '[native code]',
  `function Error() {
    [native code]
}`,
  '<unknown>',
  'unknown',
  'illegal access',
  'emitsOptions', // Vue-internal cascade symptom, all engine wordings
  "Cannot destructure property 'bum'", // Vue-internal cascade symptom, all engine wordings
  "Cannot read properties of undefined (reading 'cssMode')",
  "Can't find variable: gmo",
  'Java object is gone',
  'runtime.sendMessage',
  'Java exception',
  // TODO: Durable fix is a yarn resolutions override of hookable to >=6 (guards task.run when console.createTask is missing) so Nuxt boots in those webviews; needs testing against nuxt 3.21.1's ^5.5.3 range
  "Cannot read properties of undefined (reading 'run')", // hookable 5 when console.createTask is stubbed (bot webviews)
  '_promisesProcessed', // unhead in the same broken environments
  'The WKWebView was deallocated before the message was delivered', // iOS third-party browsers tearing down their bridge
  'WKWebView API client did not respond to this postMessage', // DuckDuckGo's injected bridge
  'Script message handler is unavailable', // DuckDuckGo's injected bridge on iOS, no stack
  'invalid origin', // DuckDuckGo Mobile's content script rejecting its own postMessage
  'Uncaught, unspecified "error" event', // @algolia/events after instantsearch dispose()
  /^Error: [A-Za-z]{2}(`prod)?$/, // Chrome iOS injected-script minified errors, e.g. "Error: La"
  'window.ethereum', // a wallet extension writing into the page's global code
  'Failed writing data to the file system', // Safari reporting its own storage failure, no stack
  "Can't find variable: IntersectionObserver", // Vue's hydrate-on-visible has no feature check; a WebView without the API
  /^Error: undefined$/, // Firefox rejects an interrupted dynamic import() with undefined; Vue's async component wrapper reports it as "Error: undefined"
  'window._META.IABInnerFrame', // Facebook browser

  // Errors that intentionally generate 404
  'Post not found:',
  'Page not found:',
  'Not found:',

  // Destination pages that could not load are kept off Sentry
  'Failed to load destination', // Solresor: a prerendered page hydrating without its payload (in-app browsers, proxies)
  'could not load _sv > _destination > _slug', // Rolfs: the page's own warning before its error page

  // A page whose useAsyncData resolved with neither data nor error: the visitor's browser cancelled the request or a
  // prerendered page hydrated without its payload. Not re-fetched by design (Nuxt 4 inlines the payload), so the pages
  // throw this one message; the 500 page's own "500 page: <message>" capture contains it too.
  MISSING_PAGE_DATA_MESSAGE,

  // Errors that occur when getting chunks from new code when still having old code in browser (common after deploying)
  "Cannot read properties of undefined (reading 'default')",
  "undefined is not an object (evaluating 'r.default')",
  "undefined is not an object (evaluating 't.default')",
  "undefined is not an object (evaluating 'e.default')",
  "undefined is not an object (evaluating 'c.default')",
  "undefined is not an object (evaluating 'o.default')",
  "undefined is not an object (evaluating '_.default')",
  "undefined is not an object (evaluating 'm.default')",
  "undefined is not an object (evaluating 'i.default')",
  "undefined is not an object (evaluating 'n.default')",
  "undefined is not an object (evaluating 'a.default')",
  'can\'t access property "default", r is undefined',
  'can\'t access property "default", e is undefined',
  'Couldn\'t resolve component "default"',
  '[GET] "/_nuxt/builds/meta/',
  'Unable to preload CSS for',
  '[nuxt] Error creating app manifest matcher. undefined',
  'error loading dynamically imported module', // older Firefox leaves out the colon and the module URL
  'Importing a module script failed.',
]

/* In-app browsers (Instagram, Facebook, Klarna) inject scripts that run as the page's own code, so
   denyUrls cannot tell their errors from ours; these function names only occur in their stacks */
export const INJECTED_SCRIPT_FUNCTIONS = [
  'dispatchToBridge', // Meta's in-app browser bridge
  'sendPostMessage',
  'logLoginFieldDetected',
  'sendDataToNative', // Meta's Android in-app browser performance logger (iabjs://navigation_performance_logger_android)
  '__AUTOMATION_EVENT_DISPATCH', // Klarna's in-app shopping browser automating the page in its Android WebView
  '__AUTOMATION_COORDINATOR',
]

// WebKit records a script the host app injected as "user-script:<n>" in the error's sourceURL
export const INJECTED_SCRIPT_SOURCE_PREFIX = 'user-script:'

export const INP_TRANSACTION_OP = 'ui.inp'

// Sentry drops a tag whose value is longer than this
export const TAG_VALUE_MAX_LENGTH = 200
